import React, { useContext, useEffect, useMemo, useState } from "react";
import Modal from "components/Modals/Modal";
import { GPUInstanceType } from "components/Environment/Settings/Tabs/Compute/InstanceChanger/GPUTypes";
import { allPublicCloudsName } from "components/Environment/Create/utils";
import { OrgContext } from "contexts/OrgContext";
import { ContainerSelected } from "components/Environment/shared/BuildTypes";
import FilterBar from "components/Environment/Create/ComputeSelector/Advanced/FilterBar";
import ComputeSelector from "components/Environment/Create/ComputeSelector";
import { AnimatePresence } from "framer-motion";
import { ExclamationTriangleIcon } from "@heroicons/react/24/solid";
import { Button } from "components/Graphs/Button";
import { XMarkIcon } from "@heroicons/react/24/outline";

export interface EditComputeModalProps {
  intendedInstanceType: string | null;
  show: boolean;
  setShow: (show: boolean) => void;
  onCancel?: () => void;
  onSuccess?: (selectedInstanceType: GPUInstanceType, storage: string) => void;
}

export const EditComputeModal: React.FC<EditComputeModalProps> = ({
  intendedInstanceType,
  show,
  setShow,
  onCancel = () => {},
  onSuccess = (selectedInstanceType: GPUInstanceType, storage: string) => {},
}) => {
  const orgContext = useContext(OrgContext);

  const [selectedInstanceType, setSelectedInstanceType] = useState<
    GPUInstanceType | undefined
  >(undefined);
  const [storage, setStorage] = useState<string>("256");

  //environment state
  const [container, setContainer] = useState<ContainerSelected | undefined>({
    vmBuild: {
      forceJupyterInstall: false,
    },
  });

  const [selectedCloudName, setSelectedCloudName] =
    useState(allPublicCloudsName);

  const [displayGPUInstances, setDisplayGPUInstances] = useState<
    GPUInstanceType[]
  >([]);

  useEffect(() => {
    if (
      selectedInstanceType?.workspace_groups &&
      selectedInstanceType.workspace_groups.length > 0
    ) {
      const firstWorkspaceGroup = selectedInstanceType.workspace_groups[0];
      if (firstWorkspaceGroup.id === "dgxc-gcp") {
        setStorage("512");
      } else {
        setStorage("256");
      }
    }
  }, [selectedInstanceType]);

  const filterForValidInstances = useMemo(() => {
    return orgContext.GPUInstancesAvailable.filter((gpu) => {
      return gpu.workspace_groups[0].id !== "dgxc-gcp";
    });
  }, [orgContext.GPUInstancesAvailable]);

  return (
    <Modal
      isOpen={show}
      setOpen={setShow}
      onClose={() => {
        setShow(false);
      }}
      title={""}
      width={"max-w-7xl"}
      body={
        <div>
          <div className="w-full flex flex-row relative">
            <XMarkIcon
              className="h-6 w-6 cursor-pointer text-red-500 absolute top-[-20px] right-[-5px]"
              onClick={() => {
                setShow(false);
                onCancel();
              }}
            />
          </div>
          <div className="flex flex-row justify-between mt-4">
            <div>
              <h2 className="text-primary text-xl font-semibold">
                Edit Compute
              </h2>
              <p className="text-secondary">
                Configure this launchable's compute resources.
              </p>
            </div>
            <div className="flex flex-row items-end">
              {container && (
                <FilterBar
                  allGPUInstances={filterForValidInstances}
                  setDisplayGPUInstances={setDisplayGPUInstances}
                  selectedCloud={selectedCloudName}
                  setSelectedCloud={setSelectedCloudName}
                  containerSelected={container}
                  displayGPUInstances={displayGPUInstances}
                />
              )}
            </div>
          </div>
          <hr className="w-[100%] my-3 dark:border dark:border-zinc-800" />
          <div className="flex flex-col">
            <AnimatePresence initial={false}>
              {container && (
                <>
                  <ComputeSelector
                    allGPUInstances={filterForValidInstances}
                    isLoadingInstances={orgContext.isInstancesLoading}
                    displayGPUInstances={displayGPUInstances}
                    setDisplayGPUInstances={setDisplayGPUInstances}
                    selectedInstance={selectedInstanceType}
                    setSelectedInstance={setSelectedInstanceType}
                    setStorage={setStorage}
                    storage={storage}
                    containerSelected={container}
                    selectedCloudName={selectedCloudName}
                    setSelectedCloudName={setSelectedCloudName}
                    hideDgxc={true}
                  />
                </>
              )}
            </AnimatePresence>
          </div>
          {selectedInstanceType &&
            intendedInstanceType !== selectedInstanceType?.type && (
              <div className="w-full mt-20 flex flex-row justify-between">
                <div className="flex flex-row justify-between">
                  <div className="flex flex-row items-center border border-red-500 rounded-md p-2">
                    <ExclamationTriangleIcon className="h-4 w-4 mr-1 text-red-500" />
                    <span className="text-sm text-red-500 font-bold">
                      This instance type is different from what the author
                      originally configured. Unexpected behavior could occur.
                    </span>
                  </div>
                </div>
                <div className="flex flex-row gap-2">
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setShow(false);
                      onCancel();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => {
                      setShow(false);
                      onSuccess(selectedInstanceType, storage);
                    }}
                  >
                    Save Compute
                  </Button>
                </div>
              </div>
            )}
        </div>
      }
      hideCancel={true}
      hideSuccess={true}
      onSuccess={() =>
        onSuccess(selectedInstanceType || ({} as GPUInstanceType), storage)
      }
      disableSuccess={!selectedInstanceType}
    />
  );
};
